import React, { useState, useMemo, useCallback } from 'react';
import {
  KABYLE_SOUNDS,
  PRONUNCIATION_RULES,
  PRACTICAL_EXPRESSIONS,
  PRONUNCIATION_QUIZ,
  KabyleSound,
  PracticalExpression
} from '../data/pronunciationData';
import { useKabyleTTS } from '../hooks/useKabyleTTS';
import { playSound } from '../audio';
import { Volume2, CheckCircle2, XCircle, RotateCcw, ChevronRight } from 'lucide-react';

interface PronunciationGuideProps {
  isAdmin?: boolean;
}

// Utility to highlight specific target letters in a word
function renderHighlightedWord(word: string, targetLetter: string) {
  if (!targetLetter) return <span>{word}</span>;
  const regex = new RegExp(`(${targetLetter}|${targetLetter.toUpperCase()})`, 'g');
  const parts = word.split(regex);
  return (
    <span>
      {parts.map((part, i) =>
        part.toLowerCase() === targetLetter.toLowerCase() ? (
          <span key={i} className="text-amber-800 font-black underline decoration-amber-600 decoration-2 underline-offset-2">
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
}

export const PronunciationGuide: React.FC<PronunciationGuideProps> = React.memo(({ isAdmin }) => {
  // Navigation inside guide
  const [activeSubTab, setActiveSubTab] = useState<'SOUNDS' | 'RULES' | 'EXPRESSIONS' | 'QUIZ'>('SOUNDS');

  // Sounds tab state
  const [soundCategoryFilter, setSoundCategoryFilter] = useState<string>('ALL');
  const [soundSearch, setSoundSearch] = useState<string>('');
  const [expandedSoundId, setExpandedSoundId] = useState<string | null>(KABYLE_SOUNDS[0]?.id || null);

  // Expressions tab state
  const [expressionCatFilter, setExpressionCatFilter] = useState<string>('ALL');
  const [expressionSearch, setExpressionSearch] = useState<string>('');

  // Quiz state
  const [quizIndex, setQuizIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [quizScore, setQuizScore] = useState(0);
  const [quizFinished, setQuizFinished] = useState(false);

  // Audio Hook
  const { playTTS, isPlaying, currentPlayingText } = useKabyleTTS();

  const handlePlay = useCallback((text: string, slow = false) => {
    playSound('click');
    playTTS(text, { slow });
  }, [playTTS]);

  // Filtered Sounds
  const filteredSounds = useMemo(() => {
    return KABYLE_SOUNDS.filter((s) => {
      const matchCat =
        soundCategoryFilter === 'ALL' ||
        (soundCategoryFilter === 'guttural' && s.category === 'guttural') ||
        (soundCategoryFilter === 'emphatic' && s.category === 'emphatic') ||
        (soundCategoryFilter === 'vowel' && s.category === 'vowel') ||
        (soundCategoryFilter === 'affricate' && s.category === 'affricate') ||
        (soundCategoryFilter === 'special' && s.category === 'special');

      if (!matchCat) return false;

      if (!soundSearch.trim()) return true;
      const term = soundSearch.toLowerCase().trim();
      return (
        s.letter.toLowerCase().includes(term) ||
        s.soundName.toLowerCase().includes(term) ||
        s.description.toLowerCase().includes(term) ||
        s.ipa.toLowerCase().includes(term) ||
        s.examples.some((ex) => ex.kab.toLowerCase().includes(term) || ex.fr.toLowerCase().includes(term))
      );
    });
  }, [soundCategoryFilter, soundSearch]);

  // Filtered Expressions
  const filteredExpressions = useMemo(() => {
    return PRACTICAL_EXPRESSIONS.filter((exp) => {
      const matchCat = expressionCatFilter === 'ALL' || exp.category === expressionCatFilter;
      if (!matchCat) return false;

      if (!expressionSearch.trim()) return true;
      const term = expressionSearch.toLowerCase().trim();
      return (
        exp.kab.toLowerCase().includes(term) ||
        exp.fr.toLowerCase().includes(term) ||
        exp.phonetic.toLowerCase().includes(term) ||
        exp.context.toLowerCase().includes(term)
      );
    });
  }, [expressionCatFilter, expressionSearch]);

  // Quiz Handling
  const currentQuestion = PRONUNCIATION_QUIZ[quizIndex];

  const handleAnswer = (chosen: string) => {
    if (selectedAnswer !== null) return;
    setSelectedAnswer(chosen);
    const isCorrect = chosen === currentQuestion.correctAnswer;
    if (isCorrect) {
      playSound('correct');
      setQuizScore((s) => s + 1);
    } else {
      playSound('wrong');
    }
  };

  const handleNextQuestion = () => {
    playSound('click');
    if (quizIndex < PRONUNCIATION_QUIZ.length - 1) {
      setQuizIndex((i) => i + 1);
      setSelectedAnswer(null);
    } else {
      setQuizFinished(true);
    }
  };

  const resetQuiz = () => {
    playSound('click');
    setQuizIndex(0);
    setSelectedAnswer(null);
    setQuizScore(0);
    setQuizFinished(false);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-parchment text-stone-900 font-mono select-none">
      {/* Sub-Tabs Bar */}
      <div className="bg-[#fcf8f0] border-b border-[#dfd2be] px-2 sm:px-4 py-1.5 shrink-0 z-10 shadow-xs">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 sm:gap-1.5 w-full">
          <button
            onClick={() => {
              playSound('click');
              setActiveSubTab('SOUNDS');
            }}
            className={`px-2 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1 cursor-pointer whitespace-nowrap border ${
              activeSubTab === 'SOUNDS'
                ? 'bg-amber-700 text-white border-amber-800 font-black shadow-xs'
                : 'bg-[#ede4d4] text-stone-700 border-[#d6c7b0] hover:bg-amber-100/70 hover:text-stone-950'
            }`}
          >
            <span>🔤</span>
            <span>Sons & Lettres</span>
            <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
              activeSubTab === 'SOUNDS' ? 'bg-amber-900/60 text-amber-100' : 'bg-[#dfd2be] text-stone-800'
            }`}>
              {KABYLE_SOUNDS.length}
            </span>
          </button>

          <button
            onClick={() => {
              playSound('click');
              setActiveSubTab('RULES');
            }}
            className={`px-2 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1 cursor-pointer whitespace-nowrap border ${
              activeSubTab === 'RULES'
                ? 'bg-amber-700 text-white border-amber-800 font-black shadow-xs'
                : 'bg-[#ede4d4] text-stone-700 border-[#d6c7b0] hover:bg-amber-100/70 hover:text-stone-950'
            }`}
          >
            <span>📖</span>
            <span>Règles d'Or</span>
            <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
              activeSubTab === 'RULES' ? 'bg-amber-900/60 text-amber-100' : 'bg-[#dfd2be] text-stone-800'
            }`}>
              {PRONUNCIATION_RULES.length}
            </span>
          </button>

          <button
            onClick={() => {
              playSound('click');
              setActiveSubTab('EXPRESSIONS');
            }}
            className={`px-2 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1 cursor-pointer whitespace-nowrap border ${
              activeSubTab === 'EXPRESSIONS'
                ? 'bg-amber-700 text-white border-amber-800 font-black shadow-xs'
                : 'bg-[#ede4d4] text-stone-700 border-[#d6c7b0] hover:bg-amber-100/70 hover:text-stone-950'
            }`}
          >
            <span>💬</span>
            <span>Phrases Utiles</span>
            <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
              activeSubTab === 'EXPRESSIONS' ? 'bg-amber-900/60 text-amber-100' : 'bg-[#dfd2be] text-stone-800'
            }`}>
              {PRACTICAL_EXPRESSIONS.length}
            </span>
          </button>

          <button
            onClick={() => {
              playSound('click');
              setActiveSubTab('QUIZ');
            }}
            className={`px-2 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1 cursor-pointer whitespace-nowrap border ${
              activeSubTab === 'QUIZ'
                ? 'bg-amber-700 text-white border-amber-800 font-black shadow-xs'
                : 'bg-[#ede4d4] text-stone-700 border-[#d6c7b0] hover:bg-amber-100/70 hover:text-stone-950'
            }`}
          >
            <span>🎯</span>
            <span>Test d'Écoute</span>
            <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
              activeSubTab === 'QUIZ' ? 'bg-amber-900/60 text-amber-100' : 'bg-[#dfd2be] text-stone-800'
            }`}>
              {PRONUNCIATION_QUIZ.length}
            </span>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto px-2.5 sm:px-5 py-3 space-y-4">
        {/* ================================================================= */}
        {/* 1. SUB-TAB: SONS ET LETTRES CLÉS                                  */}
        {/* ================================================================= */}
        {activeSubTab === 'SOUNDS' && (
          <div className="space-y-3">
            {/* Filter pills & Search */}
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center justify-between bg-[#f6f0e4] p-2.5 rounded-xl border border-[#dfd2be] shadow-2xs">
              <div className="flex gap-1 overflow-x-auto pb-0.5 scrollbar-none items-center text-[11px] font-bold">
                {[
                  { id: 'ALL', label: 'Tous les sons' },
                  { id: 'guttural', label: 'Gutturaux (ɣ, ɛ, ḥ, x, q)' },
                  { id: 'emphatic', label: 'Emphatiques (ẓ, ḍ, ṭ)' },
                  { id: 'affricate', label: 'Affriqués (č, ǧ)' },
                  { id: 'vowel', label: 'Voyelles (u, e)' }
                ].map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      playSound('click');
                      setSoundCategoryFilter(cat.id);
                    }}
                    className={`px-2.5 py-1 rounded-md whitespace-nowrap transition-all cursor-pointer border ${
                      soundCategoryFilter === cat.id
                        ? 'bg-amber-700 text-white font-black border-amber-800 shadow-2xs'
                        : 'bg-white/90 text-stone-700 border-[#d9cdbc] hover:bg-amber-50 hover:text-stone-950'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              <div className="relative min-w-[200px]">
                <input
                  type="text"
                  value={soundSearch}
                  onChange={(e) => setSoundSearch(e.target.value)}
                  placeholder="Rechercher une lettre ou un mot..."
                  className="w-full bg-white border border-[#d6c7b2] rounded-lg px-2.5 py-1.5 pl-8 text-base sm:text-xs text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-amber-700 font-mono"
                />
                <span className="absolute left-2.5 top-2 text-stone-400 text-xs">🔍</span>
                {soundSearch && (
                  <button
                    onClick={() => setSoundSearch('')}
                    className="absolute right-2 top-1.5 text-stone-500 hover:text-stone-900 text-xs bg-[#ede4d4] hover:bg-amber-100 px-1 py-0.5 rounded border border-[#d6c7b0]"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            {/* List of Sound Cards */}
            <div className="space-y-3">
              {filteredSounds.length === 0 ? (
                <div className="text-center py-8 bg-[#f6f0e4] rounded-xl border border-[#dfd2be] text-stone-600 text-xs space-y-2">
                  <p className="text-xl">🔍</p>
                  <p className="font-bold">Aucun son ne correspond à votre recherche.</p>
                  <button
                    onClick={() => {
                      setSoundSearch('');
                      setSoundCategoryFilter('ALL');
                    }}
                    className="px-3 py-1 bg-amber-700 hover:bg-amber-800 rounded text-white font-bold border border-amber-800 cursor-pointer shadow-xs"
                  >
                    Réinitialiser les filtres
                  </button>
                </div>
              ) : (
                filteredSounds.map((sound) => {
                  const isExpanded = expandedSoundId === sound.id;
                  const isSoundPlaying = currentPlayingText && sound.examples.some(e => e.kab.toLowerCase() === currentPlayingText.toLowerCase());

                  return (
                    <div
                      key={sound.id}
                      className={`rounded-xl border transition-all overflow-hidden ${
                        isExpanded
                          ? 'bg-[#fffdf9] border-2 border-amber-600 shadow-md'
                          : isSoundPlaying
                          ? 'bg-[#fffbf0] border-2 border-amber-600 shadow-[0_0_12px_rgba(217,119,6,0.2)]'
                          : 'bg-white/95 border border-[#e5dcce] hover:border-amber-600 shadow-[0_2px_6px_rgba(180,83,9,0.06)]'
                      }`}
                    >
                      {/* Sound Card Header */}
                      <div
                        onClick={() => {
                          playSound('click');
                          setExpandedSoundId(isExpanded ? null : sound.id);
                        }}
                        className="p-3 sm:p-3.5 flex items-center justify-between gap-3 cursor-pointer hover:bg-amber-50/50 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          {/* Big Letter Glyph Badge */}
                          <div className="w-12 h-12 rounded-xl bg-[#ede4d4] border-2 border-amber-700 flex flex-col items-center justify-center text-amber-950 font-black shrink-0 shadow-xs">
                            <span className="text-lg sm:text-xl leading-none text-amber-950 font-black">{sound.letter}</span>
                            <span className="text-[10px] text-amber-700 font-bold leading-none mt-0.5">
                              {sound.tifinagh}
                            </span>
                          </div>

                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="text-stone-900 font-black text-sm sm:text-base tracking-wide flex items-center gap-1.5">
                                <span>{sound.soundName}</span>
                                <span className="text-amber-800 text-xs font-mono">({sound.uppercase} / {sound.letter})</span>
                              </h3>
                              <span className="text-[10px] px-2 py-0.5 bg-[#ede4d4] text-amber-950 rounded border border-[#d6c7b0] font-bold font-mono">
                                {sound.ipa}
                              </span>
                              <span className="text-[10px] px-2 py-0.5 bg-amber-100/80 text-amber-950 rounded border border-amber-300 uppercase font-bold">
                                {sound.category}
                              </span>
                            </div>
                            <p className="text-xs text-stone-600 truncate mt-0.5">
                              {sound.description}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          {/* Quick Listen Sample */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (sound.examples[0]) {
                                handlePlay(sound.examples[0].kab);
                              }
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 cursor-pointer transition-all active:scale-95 shadow-xs border ${
                              isSoundPlaying
                                ? 'bg-amber-700 text-white border-amber-800 font-black shadow-sm animate-pulse'
                                : 'bg-amber-100 hover:bg-amber-200 text-amber-950 border border-amber-300'
                            }`}
                            title="Écouter le premier exemple"
                          >
                            <Volume2 className={`w-4 h-4 ${isSoundPlaying ? 'animate-pulse' : ''}`} />
                            <span className="hidden sm:inline">Écouter</span>
                          </button>
                          <span className="text-stone-500 text-xs px-1">{isExpanded ? '▲' : '▼'}</span>
                        </div>
                      </div>

                      {/* Expanded Detail View */}
                      {isExpanded && (
                        <div className="px-3 sm:px-4 pb-4 pt-1 border-t border-[#ede4d4] space-y-3">
                          {/* Articulation & Advice Grid */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 text-xs pt-2">
                            <div className="bg-[#fcf8f0] p-3 rounded-lg border border-[#e5dcce] space-y-1">
                              <div className="text-amber-900 font-bold uppercase tracking-wider flex items-center gap-1.5 text-[11px]">
                                <span>🗣️ Position de la bouche</span>
                              </div>
                              <p className="text-stone-700 leading-relaxed">{sound.mouthGuide}</p>
                            </div>

                            <div className="bg-rose-50/80 p-3 rounded-lg border border-rose-200 space-y-1">
                              <div className="text-rose-900 font-bold uppercase tracking-wider flex items-center gap-1.5 text-[11px]">
                                <span>⚠️ Piège fréquent</span>
                              </div>
                              <p className="text-rose-950 leading-relaxed">{sound.commonMistake}</p>
                            </div>

                            <div className="bg-emerald-50/80 p-3 rounded-lg border border-emerald-200 space-y-1">
                              <div className="text-emerald-900 font-bold uppercase tracking-wider flex items-center gap-1.5 text-[11px]">
                                <span>💡 Astuce mémotechnique</span>
                              </div>
                              <p className="text-emerald-950 leading-relaxed">{sound.tip}</p>
                            </div>
                          </div>

                          {/* Interactive Examples Section */}
                          <div className="space-y-2 pt-1">
                            <div className="flex items-center justify-between">
                              <h4 className="text-xs font-black uppercase text-amber-900 tracking-wider flex items-center gap-1.5">
                                <span>Exemples Pratiques avec Écoute</span>
                              </h4>
                              <span className="text-[11px] text-stone-500 font-normal">
                                🔊 Vitesse normale • 🐢 Ralenti (syllabes)
                              </span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {sound.examples.map((ex, idx) => {
                                const isThisPlaying = currentPlayingText?.toLowerCase() === ex.kab.toLowerCase();
                                return (
                                  <div
                                    key={idx}
                                    className={`p-3 rounded-lg border transition-all flex flex-col justify-between gap-1.5 group ${
                                      isThisPlaying
                                        ? 'bg-[#fff7e6] border-2 border-amber-600 shadow-xs'
                                        : 'bg-[#fcf8f0] hover:bg-[#fffbf2] border-[#e5dcce]'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="flex items-center gap-2.5 truncate">
                                        <span className="text-xl shrink-0">{ex.icon}</span>
                                        <div>
                                          <div className="font-black text-stone-900 text-sm sm:text-base group-hover:text-amber-900 transition-colors">
                                            {renderHighlightedWord(ex.kab, sound.letter)}
                                          </div>
                                          <div className="text-[11px] font-bold text-amber-800 tracking-wide font-mono">
                                            [{ex.phonetic}]
                                          </div>
                                        </div>
                                      </div>

                                      {/* Listen controls */}
                                      <div className="flex items-center gap-1 shrink-0">
                                        <button
                                          type="button"
                                          onClick={() => handlePlay(ex.kab, false)}
                                          className={`p-1.5 rounded-md cursor-pointer transition-all active:scale-90 border ${
                                            isThisPlaying
                                              ? 'bg-amber-700 text-white border-amber-800 animate-pulse'
                                              : 'bg-amber-100 hover:bg-amber-200 text-amber-950 border border-amber-300'
                                          }`}
                                          title="Écouter à vitesse normale"
                                        >
                                          <Volume2 className="w-4 h-4" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handlePlay(ex.kab, true)}
                                          className="p-1.5 bg-[#ede4d4] hover:bg-amber-100 text-amber-950 border border-[#d6c7b0] rounded-md cursor-pointer transition-all active:scale-90 text-xs"
                                          title="Écouter au ralenti pour bien décomposer"
                                        >
                                          🐢
                                        </button>
                                      </div>
                                    </div>

                                    <div className="text-xs text-stone-700 italic border-t border-[#ede4d4] pt-1.5 flex items-center justify-between">
                                      <span>{ex.fr}</span>
                                      {ex.note && <span className="text-[10px] text-stone-500 not-italic font-bold">({ex.note})</span>}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* ================================================================= */}
        {/* 2. SUB-TAB: RÈGLES D'OR DE PRONONCIATION                           */}
        {/* ================================================================= */}
        {activeSubTab === 'RULES' && (
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-amber-100 to-[#ede4d4] p-3.5 rounded-xl border border-amber-300 flex items-center gap-3 shadow-xs text-amber-950">
              <span className="text-3xl shrink-0">🏛️</span>
              <div>
                <h3 className="text-amber-950 font-black text-sm sm:text-base">
                  Les 4 Piliers de la Phonétique Kabyle
                </h3>
                <p className="text-xs text-stone-700 mt-0.5 leading-relaxed">
                  Comprendre ces règles fondamentales permet de lire instantanément n'importe quel texte en kabyle sans hésitation.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              {PRONUNCIATION_RULES.map((rule) => (
                <div
                  key={rule.id}
                  className="bg-white/95 border border-[#e5dcce] rounded-xl p-4 space-y-3.5 shadow-xs flex flex-col justify-between"
                >
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{rule.icon}</span>
                        <h4 className="text-stone-900 font-black text-sm sm:text-base">{rule.title}</h4>
                      </div>
                      <span className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 rounded font-bold uppercase shrink-0">
                        {rule.badge}
                      </span>
                    </div>

                    <p className="text-xs text-amber-900 font-black">{rule.summary}</p>
                    <p className="text-xs text-stone-700 leading-relaxed">{rule.explanation}</p>

                    <div className="bg-[#fcf8f0] p-3 rounded-lg border border-[#e5dcce] space-y-1.5">
                      <div className="text-[11px] font-bold text-amber-900 uppercase tracking-wider flex items-center gap-1">
                        <span>📌 À retenir :</span>
                      </div>
                      <ul className="text-xs text-stone-700 space-y-1 list-disc list-inside">
                        {rule.keyPoints.map((pt, idx) => (
                          <li key={idx} className="leading-snug">
                            {pt}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Rule Examples */}
                  <div className="space-y-2 pt-2 border-t border-[#ede4d4]">
                    <div className="text-[11px] font-bold text-stone-500 uppercase flex items-center justify-between">
                      <span>Exemples illustrés :</span>
                      <span className="text-[10px] text-stone-400">Cliquer pour écouter</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {rule.examples.map((ex, idx) => {
                        const isThisPlaying = currentPlayingText?.toLowerCase() === ex.kab.toLowerCase();
                        return (
                          <div
                            key={idx}
                            onClick={() => handlePlay(ex.kab)}
                            className={`p-2.5 rounded-lg border cursor-pointer transition-all flex items-center justify-between gap-1 group ${
                              isThisPlaying
                                ? 'bg-[#fff7e6] border-2 border-amber-600 shadow-xs'
                                : 'bg-[#fcf8f0] hover:bg-[#fffbf2] border-[#e5dcce] hover:border-amber-600'
                            }`}
                          >
                            <div className="truncate min-w-0">
                              <div className="text-xs font-black text-stone-900 group-hover:text-amber-900 truncate">
                                {ex.kab}
                              </div>
                              <div className="text-[10px] text-amber-800 font-mono">[{ex.phonetic}]</div>
                              <div className="text-[10px] text-stone-700 italic truncate">{ex.fr}</div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <Volume2 className={`w-4 h-4 ${isThisPlaying ? 'text-amber-700 animate-pulse' : 'text-stone-400 group-hover:text-amber-700'}`} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ================================================================= */}
        {/* 3. SUB-TAB: PHRASES & EXPRESSIONS PRATIQUES                       */}
        {/* ================================================================= */}
        {activeSubTab === 'EXPRESSIONS' && (
          <div className="space-y-3">
            {/* Filter pills & Search */}
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center justify-between bg-[#f6f0e4] p-2.5 rounded-xl border border-[#dfd2be] shadow-2xs">
              <div className="flex gap-1 overflow-x-auto pb-0.5 scrollbar-none items-center text-[11px] font-bold">
                {[
                  { id: 'ALL', label: 'Toutes les phrases' },
                  { id: 'SALUTATIONS', label: '👋 Salutations' },
                  { id: 'POLITESSE', label: '🤝 Politesse' },
                  { id: 'MAISON_REPAS', label: '🍲 Maison & Repas' },
                  { id: 'VILLAGE_NATURE', label: '🏔️ Village & Nature' },
                  { id: 'SAGESSE', label: '📜 Sagesse & Proverbes' }
                ].map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      playSound('click');
                      setExpressionCatFilter(cat.id);
                    }}
                    className={`px-2.5 py-1 rounded-md whitespace-nowrap transition-all cursor-pointer border ${
                      expressionCatFilter === cat.id
                        ? 'bg-amber-700 text-white font-black border-amber-800 shadow-2xs'
                        : 'bg-white/90 text-stone-700 border-[#d9cdbc] hover:bg-amber-50 hover:text-stone-950'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              <div className="relative min-w-[200px]">
                <input
                  type="text"
                  value={expressionSearch}
                  onChange={(e) => setExpressionSearch(e.target.value)}
                  placeholder="Rechercher une phrase..."
                  className="w-full bg-white border border-[#d6c7b2] rounded-lg px-2.5 py-1.5 pl-8 text-base sm:text-xs text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-amber-700 font-mono"
                />
                <span className="absolute left-2.5 top-2 text-stone-400 text-xs">🔍</span>
                {expressionSearch && (
                  <button
                    onClick={() => setExpressionSearch('')}
                    className="absolute right-2 top-1.5 text-stone-500 hover:text-stone-900 text-xs bg-[#ede4d4] hover:bg-amber-100 px-1 py-0.5 rounded border border-[#d6c7b0]"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            {/* List of Expressions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filteredExpressions.map((exp) => {
                const isThisPlaying = currentPlayingText?.toLowerCase() === exp.kab.toLowerCase();
                return (
                  <div
                    key={exp.id}
                    className={`border rounded-xl p-3.5 flex flex-col justify-between gap-2.5 shadow-xs transition-all group ${
                      isThisPlaying
                        ? 'bg-[#fff7e6] border-2 border-amber-600 shadow-sm'
                        : 'bg-white/95 hover:bg-[#fffdf9] border-[#e5dcce] hover:border-amber-600'
                    }`}
                  >
                    <div className="space-y-2">
                      {/* Top line: icon + kabyle phrase + audio buttons */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="text-2xl shrink-0">{exp.icon}</span>
                          <div>
                            <h4 className="font-black text-stone-900 text-sm sm:text-base group-hover:text-amber-900 transition-colors leading-tight">
                              {exp.kab}
                            </h4>
                            <span className="text-xs font-bold text-amber-800 font-mono">
                              [{exp.phonetic}]
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => handlePlay(exp.kab, false)}
                            className={`p-1.5 rounded-lg cursor-pointer transition-all active:scale-90 border ${
                              isThisPlaying
                                ? 'bg-amber-700 text-white border-amber-800 animate-pulse'
                                : 'bg-amber-100 hover:bg-amber-200 text-amber-950 border border-amber-300'
                            }`}
                            title="Écouter la phrase à vitesse normale"
                          >
                            <Volume2 className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handlePlay(exp.kab, true)}
                            className="p-1.5 bg-[#ede4d4] hover:bg-amber-100 text-amber-950 border border-[#d6c7b0] rounded-lg cursor-pointer transition-all active:scale-90 text-xs"
                            title="Écouter au ralenti (rythme syllabique)"
                          >
                            🐢
                          </button>
                        </div>
                      </div>

                      {/* Translation */}
                      <p className="text-xs font-bold text-stone-700 italic pt-1 border-t border-[#ede4d4]">
                        {exp.fr}
                      </p>

                      {/* Context & Cultural Note */}
                      <div className="bg-[#fcf8f0] p-2.5 rounded-lg border border-[#e5dcce] text-[11px] text-stone-700 space-y-1">
                        <div>
                          <span className="font-bold text-stone-500">Contexte : </span>
                          {exp.context}
                        </div>
                        {exp.culturalNote && (
                          <div className="text-amber-900 font-medium pt-0.5 border-t border-[#ede4d4]">
                            💡 {exp.culturalNote}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ================================================================= */}
        {/* 4. SUB-TAB: TEST D'ÉCOUTE & PRONONCIATION                          */}
        {/* ================================================================= */}
        {activeSubTab === 'QUIZ' && (
          <div className="max-w-xl mx-auto space-y-4">
            {!quizFinished ? (
              <div className="bg-white/95 border-2 border-amber-600/80 rounded-2xl p-4 sm:p-5 shadow-lg space-y-4">
                {/* Quiz Header with Step Progress */}
                <div className="space-y-2 border-b border-[#ede4d4] pb-3">
                  <div className="flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        playSound('click');
                        setActiveSubTab('SOUNDS');
                      }}
                      className="px-2.5 py-1 bg-amber-700 hover:bg-amber-800 text-white text-xs font-black uppercase tracking-wider rounded-lg border border-amber-800 shadow-xs flex items-center gap-1.5 transition-all cursor-pointer shrink-0"
                    >
                      <svg className="w-3 h-3 fill-white shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                        <polygon points="17,4 7,12 17,20" />
                      </svg>
                      <span>Retour</span>
                    </button>

                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-base sm:text-lg">🎯</span>
                      <span className="text-xs font-black uppercase text-amber-900 tracking-wider truncate">
                        Q {quizIndex + 1} / {PRONUNCIATION_QUIZ.length}
                      </span>
                    </div>

                    <div className="text-xs font-bold text-amber-900 bg-amber-100 px-2.5 py-1 rounded-full border border-amber-300 shrink-0">
                      Score : {quizScore} / {quizIndex + (selectedAnswer !== null ? 1 : 0)}
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="w-full bg-[#ede4d4] h-2 rounded-full overflow-hidden border border-[#d6c7b0]">
                    <div
                      className="bg-gradient-to-r from-amber-600 to-amber-500 h-full transition-all duration-300"
                      style={{ width: `${((quizIndex + (selectedAnswer !== null ? 1 : 0)) / PRONUNCIATION_QUIZ.length) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Question & Audio Player Card */}
                <div className="space-y-2 text-center">
                  <p className="text-sm sm:text-base font-bold text-stone-900 leading-snug">
                    {currentQuestion.question}
                  </p>

                  <div className="bg-[#fcf8f0] p-4 rounded-xl border-2 border-amber-600/40 flex flex-col items-center justify-center gap-2.5 shadow-2xs">
                    <div className="text-xs text-stone-500 uppercase tracking-wider font-bold">
                      Cliquez pour écouter l'extrait :
                    </div>
                    <div className="flex items-center gap-2.5">
                      <button
                        type="button"
                        onClick={() => handlePlay(currentQuestion.audioText, false)}
                        className={`px-4 py-2 font-black text-sm rounded-xl border flex items-center gap-2 shadow-xs active:scale-95 cursor-pointer transition-all ${
                          currentPlayingText === currentQuestion.audioText
                            ? 'bg-amber-700 text-white border-amber-800'
                            : 'bg-amber-100 hover:bg-amber-200 text-amber-950 border-amber-300'
                        }`}
                      >
                        <Volume2 className="w-5 h-5 text-amber-900" />
                        <span>Écouter (1x)</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => handlePlay(currentQuestion.audioText, true)}
                        className="px-3.5 py-2 bg-[#ede4d4] hover:bg-amber-100 text-amber-950 font-black text-xs rounded-xl border border-[#d6c7b0] flex items-center gap-1.5 active:scale-95 cursor-pointer transition-all shadow-2xs"
                        title="Écouter au ralenti"
                      >
                        <span>🐢</span>
                        <span>Ralenti</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Options List */}
                <div className="space-y-2 pt-1">
                  {currentQuestion.options.map((opt, idx) => {
                    const isSelected = selectedAnswer === opt.label;
                    const isCorrect = opt.label === currentQuestion.correctAnswer;
                    const hasAnswered = selectedAnswer !== null;

                    let btnStyle = 'bg-[#fffdf9] hover:bg-amber-50/80 text-stone-900 border-2 border-[#e5dcce] hover:border-amber-600';
                    if (hasAnswered) {
                      if (isCorrect) {
                        btnStyle = 'bg-emerald-50 border-2 border-emerald-600 text-emerald-950 font-bold shadow-xs';
                      } else if (isSelected) {
                        btnStyle = 'bg-rose-50 border-2 border-rose-600 text-rose-950 font-bold';
                      } else {
                        btnStyle = 'bg-[#ede4d4]/60 text-stone-400 border-[#d6c7b0] opacity-60';
                      }
                    }

                    return (
                      <button
                        key={idx}
                        disabled={hasAnswered}
                        onClick={() => handleAnswer(opt.label)}
                        className={`w-full p-3 rounded-xl border text-left transition-all flex items-center justify-between gap-2 cursor-pointer ${btnStyle}`}
                      >
                        <div className="min-w-0">
                          <div className="font-bold text-sm sm:text-base flex items-center gap-2 text-stone-900">
                            <span>{opt.label}</span>
                            <span className="text-xs text-amber-800 font-mono">[{opt.phonetic}]</span>
                          </div>
                          <div className="text-xs text-stone-600 italic">{opt.meaning}</div>
                        </div>

                        {hasAnswered && (
                          <div className="shrink-0">
                            {isCorrect ? (
                              <CheckCircle2 className="w-5 h-5 text-emerald-700" />
                            ) : isSelected ? (
                              <XCircle className="w-5 h-5 text-rose-700" />
                            ) : null}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Feedback & Next Button */}
                {selectedAnswer !== null && (
                  <div className="space-y-3 pt-2 border-t border-[#ede4d4]">
                    <div className="bg-[#fcf8f0] p-3 rounded-xl border border-amber-300 text-xs text-stone-800 leading-relaxed">
                      <span className="font-bold text-amber-900">💡 Explication : </span>
                      {currentQuestion.explanation}
                    </div>

                    <button
                      type="button"
                      onClick={handleNextQuestion}
                      className="w-full py-2.5 bg-amber-700 hover:bg-amber-800 text-white font-black text-xs uppercase tracking-wider rounded-xl border border-amber-800 shadow-sm active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-2"
                    >
                      <span>{quizIndex < PRONUNCIATION_QUIZ.length - 1 ? 'Question Suivante' : 'Voir le Résultat'}</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* Quiz Victory / Summary Screen */
              <div className="bg-white/95 border-2 border-amber-600 rounded-2xl p-6 text-center space-y-4 shadow-xl text-stone-900">
                <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 border-2 border-amber-600 flex items-center justify-center text-3xl shadow-xs">
                  🏆
                </div>

                <div>
                  <h3 className="text-xl font-black text-amber-950 uppercase tracking-wide">
                    Test d'Écoute Terminé !
                  </h3>
                  <p className="text-xs text-stone-600 mt-1">
                    Votre score : <span className="font-bold text-amber-900 text-base">{quizScore} / {PRONUNCIATION_QUIZ.length}</span>
                  </p>
                </div>

                <div className="bg-[#fcf8f0] p-3 rounded-xl border border-[#e5dcce] text-xs text-emerald-900 font-bold leading-relaxed">
                  {quizScore === PRONUNCIATION_QUIZ.length
                    ? '🌟 Exceptionnel ! Votre oreille kabyle est d’une remarquable précision.'
                    : quizScore >= 3
                    ? '👍 Très bon résultat ! Vous maîtrisez les sons essentiels du kabyle.'
                    : '🌱 Beau début ! N’hésitez pas à réécouter les sons et à retenter le test.'}
                </div>

                <button
                  type="button"
                  onClick={resetQuiz}
                  className="px-5 py-2.5 bg-amber-700 hover:bg-amber-800 text-white font-black text-xs uppercase tracking-wider rounded-xl border border-amber-800 shadow-sm active:scale-95 transition-all cursor-pointer inline-flex items-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>Recommencer le Test</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});
